import { create } from 'zustand';
import type { ConversationDTO, FriendDTO, FriendRequestDTO, NotificationDTO, PublicUser, ReadStateDTO } from '@clanfire/shared';
import { friendApi, notificationApi } from '@/api/endpoints';
import { useUsers } from './users';

/* ------------------------------------------------------------------ */
/* Read state (server channels)                                        */
/* ------------------------------------------------------------------ */

interface ReadStatesState {
  channels: Record<string, { lastReadAt: string; mentionCount: number; muted?: boolean }>;
  hydrate: (states: ReadStateDTO[]) => void;
  setMuted: (channelId: string, muted: boolean) => void;
  markRead: (channelId: string, at: string) => void;
  addMention: (channelId: string) => void;
  clearMentions: (channelId?: string) => void;
  reset: () => void;
}

export const useReadStates = create<ReadStatesState>((set) => ({
  channels: {},
  hydrate: (states) => set({ channels: Object.fromEntries(states.map((s) => [s.channelId, { lastReadAt: s.lastReadAt, mentionCount: s.mentionCount, muted: s.muted }])) }),
  setMuted: (channelId, muted) =>
    set((s) => {
      // A channel never read yet counts as read from now, as on the server.
      const prev = s.channels[channelId] ?? { lastReadAt: new Date().toISOString(), mentionCount: 0 };
      return { channels: { ...s.channels, [channelId]: { ...prev, muted } } };
    }),
  markRead: (channelId, at) =>
    set((s) => {
      const prev = s.channels[channelId];
      if (prev && prev.lastReadAt >= at && prev.mentionCount === 0) return s;
      return { channels: { ...s.channels, [channelId]: { ...prev, lastReadAt: prev && prev.lastReadAt > at ? prev.lastReadAt : at, mentionCount: 0 } } };
    }),
  addMention: (channelId) =>
    set((s) => {
      const prev = s.channels[channelId];
      return { channels: { ...s.channels, [channelId]: { ...prev, lastReadAt: prev?.lastReadAt ?? new Date(0).toISOString(), mentionCount: (prev?.mentionCount ?? 0) + 1 } } };
    }),
  clearMentions: (channelId) =>
    set((s) => ({
      channels: Object.fromEntries(
        Object.entries(s.channels).map(([id, v]) => [id, channelId && id !== channelId ? v : { ...v, mentionCount: 0 }]),
      ),
    })),
  reset: () => set({ channels: {} }),
}));

/* ------------------------------------------------------------------ */
/* Conversations (DMs)                                                 */
/* ------------------------------------------------------------------ */

interface ConversationsState {
  conversations: Record<string, ConversationDTO>;
  hydrate: (list: ConversationDTO[]) => void;
  upsert: (conversation: ConversationDTO) => void;
  /** A group's name, owner or members changed (`participants` includes you). */
  applyGroupUpdate: (update: { id: string; name: string | null; ownerId: string | null; participants: PublicUser[] }, myId: string | undefined) => void;
  remove: (id: string) => void;
  touch: (id: string, messageId: string, at: string, fromMe: boolean) => void;
  markRead: (id: string, at: string) => void;
  reset: () => void;
}

export const useConversations = create<ConversationsState>((set) => ({
  conversations: {},
  hydrate: (list) => {
    useUsers.getState().upsert(list.flatMap((c) => c.recipients));
    set({ conversations: Object.fromEntries(list.map((c) => [c.id, c])) });
  },
  upsert: (conversation) => {
    useUsers.getState().upsert(conversation.recipients);
    set((s) => ({ conversations: { ...s.conversations, [conversation.id]: conversation } }));
  },
  applyGroupUpdate: (update, myId) => {
    useUsers.getState().upsert(update.participants);
    set((s) => {
      const c = s.conversations[update.id];
      if (!c) return s;
      const recipients = update.participants.filter((u) => u.id !== myId);
      return { conversations: { ...s.conversations, [update.id]: { ...c, name: update.name, ownerId: update.ownerId, recipients } } };
    });
  },
  remove: (id) =>
    set((s) => {
      const { [id]: _removed, ...rest } = s.conversations;
      return { conversations: rest };
    }),
  touch: (id, messageId, at, fromMe) =>
    set((s) => {
      const c = s.conversations[id];
      if (!c) return s;
      return {
        conversations: {
          ...s.conversations,
          [id]: { ...c, lastMessageId: messageId, lastMessageAt: at, unreadCount: fromMe ? 0 : c.unreadCount + 1, lastReadAt: fromMe ? at : c.lastReadAt },
        },
      };
    }),
  markRead: (id, at) =>
    set((s) => {
      const c = s.conversations[id];
      if (!c || (c.unreadCount === 0 && c.lastReadAt >= at)) return s;
      return { conversations: { ...s.conversations, [id]: { ...c, unreadCount: 0, lastReadAt: at } } };
    }),
  reset: () => set({ conversations: {} }),
}));

/* ------------------------------------------------------------------ */
/* Friends                                                             */
/* ------------------------------------------------------------------ */

interface FriendsState {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  friends: Record<string, FriendDTO>;
  requests: Record<string, FriendRequestDTO>;
  /** From bootstrap, used until the full list has been loaded. */
  pendingCount: number;
  load: (force?: boolean) => Promise<void>;
  addRequest: (request: FriendRequestDTO) => void;
  removeRequest: (requestId: string) => void;
  addFriend: (friend: FriendDTO) => void;
  removeFriend: (userId: string) => void;
  setPendingCount: (n: number) => void;
  reset: () => void;
}

export const useFriends = create<FriendsState>((set, get) => ({
  status: 'idle',
  friends: {},
  requests: {},
  pendingCount: 0,
  load: async (force = false) => {
    if (!force && (get().status === 'loading' || get().status === 'loaded')) return;
    set({ status: 'loading' });
    try {
      const data = await friendApi.list();
      useUsers.getState().upsert([...data.friends.map((f) => f.user), ...data.incoming.map((r) => r.user), ...data.outgoing.map((r) => r.user)]);
      set({
        status: 'loaded',
        friends: Object.fromEntries(data.friends.map((f) => [f.user.id, f])),
        requests: Object.fromEntries([...data.incoming, ...data.outgoing].map((r) => [r.id, r])),
        pendingCount: data.incoming.length,
      });
    } catch {
      set({ status: 'error' });
    }
  },
  addRequest: (request) => {
    useUsers.getState().upsert([request.user]);
    set((s) => ({
      requests: { ...s.requests, [request.id]: request },
      pendingCount: request.direction === 'incoming' && !s.requests[request.id] ? s.pendingCount + 1 : s.pendingCount,
    }));
  },
  removeRequest: (requestId) =>
    set((s) => {
      const request = s.requests[requestId];
      const { [requestId]: _removed, ...requests } = s.requests;
      return { requests, pendingCount: request?.direction === 'incoming' ? Math.max(0, s.pendingCount - 1) : s.pendingCount };
    }),
  addFriend: (friend) => {
    useUsers.getState().upsert([friend.user]);
    set((s) => ({ friends: { ...s.friends, [friend.user.id]: friend } }));
  },
  removeFriend: (userId) =>
    set((s) => {
      const { [userId]: _removed, ...friends } = s.friends;
      return { friends };
    }),
  setPendingCount: (n) => set({ pendingCount: n }),
  reset: () => set({ status: 'idle', friends: {}, requests: {}, pendingCount: 0 }),
}));

/* ------------------------------------------------------------------ */
/* Notifications inbox                                                 */
/* ------------------------------------------------------------------ */

interface NotificationsState {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  items: NotificationDTO[];
  unreadCount: number;
  hasMore: boolean;
  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  add: (n: NotificationDTO) => void;
  markRead: (ids: string[] | 'all', channelId?: string) => void;
  setUnreadCount: (n: number) => void;
  reset: () => void;
}

export const useNotifications = create<NotificationsState>((set, get) => ({
  status: 'idle',
  items: [],
  unreadCount: 0,
  hasMore: false,
  load: async () => {
    set({ status: 'loading' });
    try {
      const { notifications, unreadCount } = await notificationApi.list();
      useUsers.getState().upsert(notifications.map((n) => n.actor));
      set({ status: 'loaded', items: notifications, unreadCount, hasMore: notifications.length >= 30 });
    } catch {
      set({ status: 'error' });
    }
  },
  loadMore: async () => {
    const last = get().items.at(-1);
    if (!last || !get().hasMore) return;
    const { notifications } = await notificationApi.list(last.id);
    set((s) => ({ items: [...s.items, ...notifications], hasMore: notifications.length >= 30 }));
  },
  add: (n) => {
    useUsers.getState().upsert([n.actor]);
    set((s) => ({ items: [n, ...s.items.filter((x) => x.id !== n.id)], unreadCount: s.unreadCount + (n.readAt ? 0 : 1) }));
  },
  /**
   * Marks inbox items read. Channel-scoped reads leave `unreadCount` alone: that is
   * adjusted from the channel's mention count (see lib/readState.ts), which is
   * known even when the inbox hasn't been loaded.
   */
  markRead: (ids, channelId) =>
    set((s) => {
      const now = new Date().toISOString();
      let cleared = 0;
      const items = s.items.map((n) => {
        const match = ids === 'all' ? !channelId || n.channelId === channelId : ids.includes(n.id);
        if (match && !n.readAt) {
          cleared++;
          return { ...n, readAt: now };
        }
        return n;
      });
      let unreadCount = s.unreadCount;
      if (ids === 'all' && !channelId) unreadCount = 0;
      else if (ids !== 'all') unreadCount = Math.max(0, s.unreadCount - cleared);
      return { items, unreadCount };
    }),
  setUnreadCount: (n) => set({ unreadCount: n }),
  reset: () => set({ status: 'idle', items: [], unreadCount: 0, hasMore: false }),
}));
